import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Loader2, ChevronDown, Check, Volume2, Sparkles } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { videoApiClient } from '@/services/videoApi';

/**
 * Narration voice picker.
 *
 * Collapses to a single select-style control that opens a compact, scrollable
 * list grouped by gender, with an inline ▶ preview per voice (preview before
 * you pick). "Automatic" (= null) is the default. Previews use a plain Audio
 * element, so cross-origin samples (public GCS/CDN) play without any CORS setup.
 */
export default function VoicePicker({ value, onChange, disabled = false }) {
    const [voices, setVoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [open, setOpen] = useState(false);
    const [playingId, setPlayingId] = useState(null);
    const audioRef = useRef(null);

    useEffect(() => {
        let active = true;
        videoApiClient.listVoices()
            .then((res) => { if (active) setVoices(res?.voices || []); })
            .catch(() => { if (active) setError(true); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    const stopAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setPlayingId(null);
    };

    // Stop playback when the menu closes, the picker is disabled, or on unmount.
    useEffect(() => { if (disabled || !open) stopAudio(); }, [disabled, open]);
    useEffect(() => () => stopAudio(), []);

    const playPreview = (voice) => {
        if (!voice?.sample_url) return;
        if (playingId === voice.id) { stopAudio(); return; }
        if (!audioRef.current) {
            audioRef.current = new Audio();
            audioRef.current.addEventListener('ended', () => setPlayingId(null));
        }
        audioRef.current.src = voice.sample_url;
        audioRef.current.play()
            .then(() => setPlayingId(voice.id))
            .catch(() => setPlayingId(null));
    };

    const selected = voices.find((v) => v.id === value) || null;
    const female = voices.filter((v) => v.gender === 'female');
    const male = voices.filter((v) => v.gender === 'male');

    const voiceRow = (voice) => {
        const isSel = value === voice.id;
        const isPlaying = playingId === voice.id;
        return (
            <div key={voice.id} className={cn('flex items-center gap-1 rounded-lg', isSel && 'bg-coral/5')}>
                <button
                    type="button"
                    disabled={!voice.sample_url}
                    onClick={() => playPreview(voice)}
                    aria-label={isPlaying ? `Stop ${voice.name} preview` : `Preview ${voice.name}`}
                    className={cn(
                        'my-1 ml-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors',
                        isPlaying
                            ? 'border-coral bg-coral/10 text-coral'
                            : 'border-border text-muted-foreground hover:border-coral/40 hover:text-coral',
                        !voice.sample_url && 'cursor-not-allowed opacity-40',
                    )}
                >
                    {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 translate-x-px" />}
                </button>
                <button
                    type="button"
                    onClick={() => { onChange(voice.id); setOpen(false); }}
                    className={cn(
                        'flex flex-1 items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors',
                        isSel ? 'font-medium text-coral' : 'text-foreground hover:bg-muted',
                    )}
                >
                    <span className="truncate">{voice.name}</span>
                    {isSel && <Check className="h-4 w-4 shrink-0" />}
                </button>
            </div>
        );
    };

    const sectionHeader = (text) => (
        <p className="sticky top-0 z-10 bg-popover px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            {text}
        </p>
    );

    return (
        <div className="space-y-2">
            <Label className="text-sm font-medium">Narration voice</Label>

            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        disabled={disabled || loading}
                        className={cn(
                            'flex w-full items-center gap-2 rounded-xl border-2 px-3.5 py-2.5 text-sm transition-all',
                            'border-border hover:border-muted-foreground/30',
                            'disabled:cursor-not-allowed disabled:opacity-60',
                            open && 'border-coral',
                        )}
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                        ) : selected ? (
                            <Volume2 className="h-4 w-4 shrink-0 text-coral" />
                        ) : (
                            <Sparkles className="h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <span className={cn('flex-1 truncate text-left', selected ? 'font-medium text-foreground' : 'text-muted-foreground')}>
                            {loading ? 'Loading voices…' : selected ? selected.name : 'Automatic — let CornerCast choose'}
                        </span>
                        {selected && (
                            <span className="shrink-0 text-xs capitalize text-muted-foreground">{selected.gender}</span>
                        )}
                        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                </PopoverTrigger>

                <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[15rem] p-1">
                    <div className="max-h-72 overflow-y-auto">
                        {/* Automatic (default) */}
                        <button
                            type="button"
                            onClick={() => { onChange(null); setOpen(false); }}
                            className={cn(
                                'flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors',
                                value == null ? 'bg-coral/5 font-medium text-coral' : 'text-foreground hover:bg-muted',
                            )}
                        >
                            <span className="flex items-center gap-2">
                                <Sparkles className="h-4 w-4 shrink-0" />
                                Automatic — let CornerCast choose
                            </span>
                            {value == null && <Check className="h-4 w-4 shrink-0" />}
                        </button>

                        {error && (
                            <p className="px-2 py-2 text-xs text-muted-foreground">
                                Voice previews are unavailable right now — automatic casting will be used.
                            </p>
                        )}

                        {female.length > 0 && (<>{sectionHeader('Female')}{female.map(voiceRow)}</>)}
                        {male.length > 0 && (<>{sectionHeader('Male')}{male.map(voiceRow)}</>)}
                    </div>
                </PopoverContent>
            </Popover>

            <p className="text-xs text-muted-foreground">
                Preview and choose a narration voice, or let CornerCast cast it for you.
            </p>
        </div>
    );
}
